import { ReactNode, useEffect, useState } from "react";
import wrapperHOC from "@Utils/wrapperHOC";
import Image from "@Components/Image";
import { CardIcon } from "./cardAssets";
import styles from "../neighbourhoodSection.module.scss";

type NeighbourhoodCardProps = {
  /** Header image, rendered as the top band of the card. */
  image: string;
  /** Circular badge icon overlapping the bottom-left of the header image. */
  icon: CardIcon;
  /** Green uppercase eyebrow, e.g. "Living cost". */
  label: string;
  /** Headline figure, e.g. "4% lower" or "82". */
  stat?: string;
  /** Supporting line under the headline, e.g. "Out of 100". */
  subtext?: string;
  /**
   * Bottom strip. Absent → neither the strip nor its divider renders, so a
   * missing data point leaves no empty row behind (P1D-5482).
   */
  footer?: ReactNode;
  alt?: string;
};

// Tablet viewport band (inclusive) that should use the "-tablet" header variant.
const TABLET_MIN_WIDTH = 479;
const TABLET_MAX_WIDTH = 899;

/**
 * Insert a "-tablet" suffix before the file extension of the header image URL,
 * preserving any query string. e.g.
 *   .../climate.png?w=600&auto=format  →  .../climate-tablet.png?w=600&auto=format
 */
const toTabletImage = (url: string): string => {
  const [path, query] = url.split("?");
  const tabletPath = path.replace(/(\.[a-zA-Z0-9]+)$/, "-tablet$1");
  return query ? `${tabletPath}?${query}` : tabletPath;
};

const NeighbourhoodCard = ({
  image,
  icon,
  label,
  stat,
  subtext,
  footer,
  alt = "Neighbourhood",
}: NeighbourhoodCardProps) => {
  // Swap to the tablet variant between 479px and 899px. Guarded so SSR/first
  // paint uses the default image (no hydration mismatch); the effect updates it
  // on the client and on resize.
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const check = () => {
      const w = window.innerWidth;
      setIsTablet(w >= TABLET_MIN_WIDTH && w <= TABLET_MAX_WIDTH);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const resolvedImage = isTablet ? toTabletImage(image) : image;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <Image
          src={resolvedImage}
          alt={alt}
          className={styles.cardHeaderImage}
          width="100%"
          height="100%"
          isEagerLoad
        />
      </div>

      <div className={styles.cardIconBadge}>
        <Image
          src={icon.src}
          alt=""
          className={styles.cardIcon}
          width={icon.width}
          height={icon.height}
          isEagerLoad
        />
      </div>

      <div className={styles.cardBody}>
        <p className={styles.cardLabel}>{label}</p>
        {stat && <p className={styles.cardStat}>{stat}</p>}
        {subtext && <p className={styles.cardSubtext}>{subtext}</p>}
      </div>

      {footer && <div className={styles.cardFooter}>{footer}</div>}
    </div>
  );
};

export default wrapperHOC(NeighbourhoodCard, {
  componentName: "NeighbourhoodCard",
  showForChina: false,
  showForWhiteLable: false,
});
